// ---------------------------------------------------------------------------
// useQualityControlMatrices
//
// Thin wrapper around `useApi` that fetches the bulk Quality Control matrices
// envelope (`GET /api/management-view/quality-control`) for the active
// scope/window/cadence filters. Refetches whenever any filter value changes.
//
// The hook does NOT serialise the filters into URL params itself — that lives
// in `management-view-quality-control-api`. We only thread the values through
// `useApi`'s dependency array so a new request fires on each filter change.
//
// Per US5 (PRODOPSAI-398): when filters change while a request is in flight,
// the latest request wins. `useApi` captures the fetcher closure on each
// dependency change and the rendered state always reflects the most recent
// `load()` call's result, so prior in-flight responses are effectively
// discarded by React's render scheduling.
// ---------------------------------------------------------------------------

import { useApi, type UseApiResult } from './useApi';
import { managementViewQualityControlApi } from '@/services/management-view-quality-control-api';
// scopeDepKey lives in the shared query module (S7721-safe module scope).
import { scopeDepKey, type IManagementViewQuery } from '@/lib/management-view-query';
import type { IQualityControlMatricesResponse } from '@skystone/shared';

export type IUseQualityControlMatricesArgs = IManagementViewQuery;

export function useQualityControlMatrices(
  args: IUseQualityControlMatricesArgs,
): UseApiResult<IQualityControlMatricesResponse> {
  const { scope, window, cadence, includeArchived } = args;
  return useApi<IQualityControlMatricesResponse>(
    () => managementViewQualityControlApi.getMatrices({ scope, window, cadence, includeArchived }),
    [scopeDepKey(scope), window, cadence, includeArchived === true],
  );
}
